/**
 * @file DuFormComponents
 * @author sunwen05
 */

import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {
    Input,
    Select,
    DatePicker,
    Cascader,
    Radio,
    Button,
    InputNumber,
    Checkbox,
    Switch,
    TreeSelect,
    Transfer
} from 'acud';

import FileUpload from '../../../common/FileUpload';
import {DuFormItemProps} from '../index';

const {Button: RadioButton, Group: RadioGroup} = Radio;
const {Group: CheckboxGroup} = Checkbox;
const Option = Select.Option; // 解构写法打包后出错待跟进
const {MonthPicker, RangePicker, WeekPicker} = DatePicker;
const TextArea = Input.TextArea;

/**
 * 表单项内组件
 *
 * @param {Object} props props
 * @return {Object} tpl<formItem>
 */

interface Props extends DuFormItemProps {
    isHasQueryPanel?: boolean;
}
const DuFormComponents: React.FunctionComponent<Props> = props => {
    const {control, option, formData, isHasQueryPanel = false, ...formControl} = props;
    const isSubmiting = option.isSubmiting;

    // 基本配置
    const {type, name, disabled, content, events = {}, style = {}, config = {}} = control;
    // 默认值
    const defaultValue = _.get(formData, name, control.defaultValue);

    // onChange需要合并
    const onChange = (e: any) => {
        events && events.onChange && events.onChange(e);
        formControl.onChange && formControl.onChange(e);
    };

    // form组件配置项
    const itemConfig = {
        disabled,
        style, // 样式
        ..._.omit(config, 'subType'), // 个性化设置
        ...events, // 事件
        ...formControl,
        onChange
    };

    if (type === 'text') {
        return defaultValue;
    }
    // 日历
    else if (type === 'rangePicker') {
        if (config.subType === 'date') {
            return <DatePicker {...itemConfig} />;
        }
        else if (config.subType === 'month') {
            return <MonthPicker {...itemConfig} />;
        }
        else if (config.subType === 'week') {
            return <WeekPicker {...itemConfig} />;
        }
        return <RangePicker {...itemConfig} />;
    }
    // 输入框
    else if (type === 'input') {
        let inputComponent = null as React.ReactNode;
        if (config.subType === 'number') {
            inputComponent = (<InputNumber {...itemConfig} />);
        }
        else if (!isHasQueryPanel && config.subType === 'textarea') {
            inputComponent = (<TextArea rows={4} {...itemConfig} />);
        }
        else if (!isHasQueryPanel && config.subType === 'password') {
            inputComponent = (<Input.Password {...itemConfig} />);
        }
        else {
            inputComponent = (<Input type={config.subType} {...itemConfig} />);
        }
        return inputComponent;
    }
    // 下拉框
    else if (type === 'select') {
        delete itemConfig.options;
        return (
            <Select
                mode={config.mode}
                {...itemConfig}
            >
                {config.options && config.options.map((item: any) => (
                    <Option
                        key={item.value}
                        value={item.value}
                        title={item.text || item.label}
                        disabled={item.disabled}
                        extra={item.extra}
                    >
                        {item.text || item.label}
                    </Option>
                ))}
            </Select>
        );
    }
    // 处理radio选择框
    else if (!isHasQueryPanel && type === 'radio') {
        // 不要传options到RadioGroup中，不然RadioButton就无效了
        _.unset(itemConfig, 'options');
        const isButton = config.subType === 'button';
        const RadioItem = isButton ? RadioButton : Radio;
        return (
            <RadioGroup {...itemConfig} buttonStyle="solid">
                {config.options.map((item: any) => (
                    <RadioItem
                        key={item.value}
                        value={item.value}
                        checked={item.value}
                        disabled={item.disabled}
                        limit={isButton ? item.limit : undefined}
                    >
                        {item.label || item.text}
                    </RadioItem>
                ))}
            </RadioGroup>
        );
    }
    // 处理checkbox选择框
    else if (!isHasQueryPanel && type === 'checkbox') {
        return (
            (config.options) ? (
                <CheckboxGroup {...itemConfig} />
            ) : (
                <Checkbox {...itemConfig}>
                    {config.label}
                </Checkbox>
            )
        );
    }
    // 级联选择器
    else if (type === 'cascader') {
        return (
            <Cascader {...itemConfig} />
        );
    }
    // 上传
    else if (!isHasQueryPanel && type === 'upload') {
        return (
            <FileUpload {...itemConfig} />
        );
    }
    else if (!isHasQueryPanel && type === 'custom') {
        return content;
    }
    else if (!isHasQueryPanel && type === 'button') {
        return (
            <div key="button">
                {
                    config.map((item: any) => (
                        <Button
                            loading={isSubmiting}
                            key={item.type}
                            type={item.skin}
                            htmlType={item.type}
                            {...item.config}
                            {...item.events}
                        >
                            {item.text}
                        </Button>
                    ))
                }
            </div>
        );
    }
    else if (!isHasQueryPanel && type === 'switch') {
        return <Switch {...itemConfig} />;
    }
    else if (type === 'treeSelect') {
        return <TreeSelect {...itemConfig} />;
    }
    else if (type === 'transfer') {
        return <Transfer {...itemConfig} />;
    }

    return <div>{content}</div>;
};

DuFormComponents.propTypes = {
    control: PropTypes.object.isRequired,
    option: PropTypes.object.isRequired,
    formData: PropTypes.object
};

DuFormComponents.defaultProps = {
    formData: null
};

export default DuFormComponents;
